/**
 * Affiliate portal — magic-link auth + dashboard against the Worker API.
 */
(function () {
  const SESSION_KEY = 'ovani_aff_session';

  function money(n, currency) {
    const v = Number(n) || 0;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD',
      }).format(v);
    } catch {
      return '$' + v.toFixed(2);
    }
  }

  function esc(s) {
    return String(s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function initRoot(root) {
    const workerUrl = (root.dataset.workerUrl || '').replace(/\/$/, '');
    const loginPanel = root.querySelector('[data-ovani-aff-login]');
    const dashPanel = root.querySelector('[data-ovani-aff-dashboard]');
    const loginForm = root.querySelector('[data-ovani-aff-login-form]');
    const loginStatus = root.querySelector('[data-ovani-aff-login-status]');
    const emailInput = root.querySelector('[data-ovani-aff-email]');
    const logoutBtn = root.querySelector('[data-ovani-aff-logout]');
    const welcome = root.querySelector('[data-ovani-aff-welcome]');
    const rateEl = root.querySelector('[data-ovani-aff-rate]');
    const linkInput = root.querySelector('[data-ovani-aff-link]');
    const codeInput = root.querySelector('[data-ovani-aff-code]');
    const statsEl = root.querySelector('[data-ovani-aff-stats]');
    const commissionsEl = root.querySelector('[data-ovani-aff-commissions]');
    const payoutsEl = root.querySelector('[data-ovani-aff-payouts]');
    const profileForm = root.querySelector('[data-ovani-aff-profile-form]');
    const profileStatus = root.querySelector('[data-ovani-aff-profile-status]');
    const methodInput = root.querySelector('[data-ovani-aff-payout-method]');
    const detailsInput = root.querySelector('[data-ovani-aff-payout-details]');
    const payoutBtn = root.querySelector('[data-ovani-aff-request-payout]');
    const payoutStatus = root.querySelector('[data-ovani-aff-payout-status]');

    function getSession() {
      try {
        return localStorage.getItem(SESSION_KEY) || '';
      } catch {
        return '';
      }
    }

    function setSession(id) {
      try {
        if (id) localStorage.setItem(SESSION_KEY, id);
        else localStorage.removeItem(SESSION_KEY);
      } catch (_) {}
    }

    async function api(path, { method, body, auth } = {}) {
      const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
      if (auth !== false) {
        const session = getSession();
        if (session) headers.Authorization = 'Bearer ' + session;
      }
      const res = await fetch(workerUrl + path, {
        method: method || (body ? 'POST' : 'GET'),
        headers,
        body: body != null ? JSON.stringify(body) : undefined,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || res.statusText);
      return data;
    }

    function showLoggedIn(on) {
      if (loginPanel) loginPanel.hidden = on;
      if (dashPanel) dashPanel.hidden = !on;
    }

    function renderStats(stats) {
      if (!statsEl) return;
      const items = [
        ['Clicks', stats.clicks],
        ['Orders', stats.orders],
        ['Pending', money(stats.pending)],
        ['Approved', money(stats.approved)],
        ['Paid', money(stats.paid)],
      ];
      statsEl.innerHTML = items
        .map(
          ([label, value]) =>
            `<div class="ovani-aff__stat"><span class="ovani-aff__stat-value">${esc(
              value
            )}</span><span class="ovani-aff__stat-label">${esc(label)}</span></div>`
        )
        .join('');
    }

    function renderCommissions(rows) {
      if (!commissionsEl) return;
      if (!rows || !rows.length) {
        commissionsEl.innerHTML = '<p class="ovani-aff__empty">No referred orders yet.</p>';
        return;
      }
      commissionsEl.innerHTML = `
        <table class="ovani-aff__table">
          <thead>
            <tr><th>Order</th><th>Customer paid</th><th>Your cut</th><th>Status</th><th>Date</th></tr>
          </thead>
          <tbody>
            ${rows
              .map(
                (c) => `
              <tr>
                <td>${esc(c.order_name || c.shopify_order_id)}</td>
                <td>${esc(money(c.order_total_paid, c.currency))}</td>
                <td>${esc(money(c.commission_amount, c.currency))}</td>
                <td>${esc(c.status)}</td>
                <td>${esc((c.created_at || '').slice(0, 10))}</td>
              </tr>`
              )
              .join('')}
          </tbody>
        </table>`;
    }

    function renderPayouts(rows) {
      if (!payoutsEl) return;
      if (!rows || !rows.length) {
        payoutsEl.innerHTML = '<p class="ovani-aff__empty">No payout requests yet.</p>';
        return;
      }
      payoutsEl.innerHTML = `
        <table class="ovani-aff__table">
          <thead>
            <tr><th>Amount</th><th>Status</th><th>Requested</th><th>Notes</th></tr>
          </thead>
          <tbody>
            ${rows
              .map(
                (p) => `
              <tr>
                <td>${esc(money(p.amount))}</td>
                <td>${esc(p.status)}</td>
                <td>${esc((p.created_at || '').slice(0, 10))}</td>
                <td>${esc(p.notes || p.admin_notes || '')}</td>
              </tr>`
              )
              .join('')}
          </tbody>
        </table>`;
    }

    async function loadDashboard() {
      const data = await api('/affiliate/me');
      const a = data.affiliate;
      if (welcome) welcome.textContent = 'Hi, ' + (a.name || a.email);
      if (rateEl) {
        rateEl.textContent =
          Math.round(Number(a.commission_rate) * 100) +
          '% of what customers pay (after discounts)';
      }
      if (linkInput) linkInput.value = data.referral_url || '';
      if (codeInput) codeInput.value = a.code || '';
      if (methodInput) methodInput.value = a.payout_method || '';
      if (detailsInput) detailsInput.value = a.payout_details || '';
      renderStats(data.stats || {});
      renderCommissions(data.commissions || []);
      renderPayouts(data.payouts || []);
      showLoggedIn(true);
    }

    async function consumeMagicTokenFromUrl() {
      const params = new URLSearchParams(location.search);
      const token = params.get('token');
      if (!token) return false;
      if (loginStatus) loginStatus.textContent = 'Signing you in…';
      const data = await api('/affiliate/auth/verify', {
        method: 'POST',
        body: { token },
        auth: false,
      });
      setSession(data.session_id);
      try {
        const url = new URL(location.href);
        url.searchParams.delete('token');
        history.replaceState({}, '', url.pathname + url.search + url.hash);
      } catch (_) {}
      return true;
    }

    async function boot() {
      if (!workerUrl) {
        if (loginStatus) loginStatus.textContent = 'Worker URL is not configured.';
        return;
      }
      try {
        await consumeMagicTokenFromUrl();
        if (getSession()) {
          await loadDashboard();
        } else {
          showLoggedIn(false);
        }
      } catch (err) {
        setSession('');
        showLoggedIn(false);
        if (loginStatus) loginStatus.textContent = err.message || 'Could not sign in.';
      }
    }

    if (loginForm) {
      loginForm.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (loginStatus) loginStatus.textContent = 'Sending…';
        try {
          const data = await api('/affiliate/auth/request', {
            method: 'POST',
            body: { email: emailInput && emailInput.value },
            auth: false,
          });
          if (loginStatus) {
            loginStatus.textContent = data.email_configured
              ? data.message
              : data.message + ' (Email delivery is not configured on the server yet.)';
          }
        } catch (err) {
          if (loginStatus) loginStatus.textContent = err.message || 'Request failed';
        }
      });
    }

    if (logoutBtn) {
      logoutBtn.addEventListener('click', async () => {
        try {
          await api('/affiliate/auth/logout', { method: 'POST', body: {} });
        } catch (_) {}
        setSession('');
        showLoggedIn(false);
        if (loginStatus) loginStatus.textContent = 'Signed out.';
      });
    }

    root.querySelector('[data-ovani-aff-copy-link]')?.addEventListener('click', async () => {
      if (!linkInput?.value) return;
      try {
        await navigator.clipboard.writeText(linkInput.value);
      } catch (_) {
        linkInput.select();
        document.execCommand('copy');
      }
    });

    root.querySelector('[data-ovani-aff-copy-code]')?.addEventListener('click', async () => {
      if (!codeInput?.value) return;
      try {
        await navigator.clipboard.writeText(codeInput.value);
      } catch (_) {
        codeInput.select();
        document.execCommand('copy');
      }
    });

    if (profileForm) {
      profileForm.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (profileStatus) profileStatus.textContent = 'Saving…';
        try {
          await api('/affiliate/me/profile', {
            method: 'POST',
            body: {
              payout_method: methodInput && methodInput.value,
              payout_details: detailsInput && detailsInput.value,
            },
          });
          if (profileStatus) profileStatus.textContent = 'Saved.';
        } catch (err) {
          if (profileStatus) profileStatus.textContent = err.message || 'Save failed';
        }
      });
    }

    if (payoutBtn) {
      payoutBtn.addEventListener('click', async () => {
        if (payoutStatus) payoutStatus.textContent = 'Submitting…';
        try {
          const data = await api('/affiliate/me/payout', {
            method: 'POST',
            body: {
              notes: 'Portal request',
              payout_method: methodInput && methodInput.value,
              payout_details: detailsInput && detailsInput.value,
            },
          });
          if (payoutStatus) {
            payoutStatus.textContent =
              'Requested ' + money(data.amount) + '. We’ll pay you manually and mark it paid in admin.';
          }
          await loadDashboard();
        } catch (err) {
          if (payoutStatus) payoutStatus.textContent = err.message || 'Request failed';
        }
      });
    }

    boot();
  }

  function bootAll() {
    document.querySelectorAll('[data-ovani-affiliate-portal]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }
})();
